import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { DatePipe } from "@angular/common";
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogActions,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from "@angular/material/dialog";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTooltip } from "@angular/material/tooltip";
import { TranslatePipe, TranslateService } from "@tolgee/ngx";
import { finalize, first } from "rxjs";
import { NamePipe } from "../../../../pipes/name.pipe";
import { MitpManagerService } from "../../../../services/mitp-manager.service";
import { NotificationService } from "../../../../services/notification.service";
import { UserService } from "../../../../services/user.service";
import { ProtocolNote } from "../entities/protocol-note";
import { isTextQuoteAnchor } from "../entities/protocol-note-anchor";
import { ProtocolNoteService } from "../protocol-note.service";
import {
	ProtocolNoteDialogComponent,
	ProtocolNoteDialogData,
} from "../protocol-note-dialog/protocol-note-dialog.component";
import { HelpButtonComponent } from "../../../help-center/help-button/help-button.component";

export interface ProtocolNotesListData {
	protocolId: number;
	/** Mise en avant à l'ouverture, quand on arrive depuis un marqueur du protocole. */
	focusNoteId?: number;
}

/**
 * Toujours renvoyé à la fermeture : le visualiseur doit savoir s'il faut
 * recharger les notes, et le cas échéant se recaler sur le passage ancré.
 */
export interface ProtocolNotesListResult {
	action: "locate" | "close";
	changed: boolean;
	note?: ProtocolNote;
}

@Component({
	selector: "app-protocol-notes-list",
	imports: [
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatButton,
		MatIconButton,
		MatIcon,
		MatProgressSpinner,
		MatTooltip,
		DatePipe,
		NamePipe,
		TranslatePipe,
		HelpButtonComponent,
	],
	templateUrl: "./protocol-notes-list.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: "./protocol-notes-list.component.scss",
})
export class ProtocolNotesListComponent {
	readonly data = inject<ProtocolNotesListData>(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<ProtocolNotesListComponent>);
	private dialog = inject(MatDialog);
	private noteService = inject(ProtocolNoteService);
	private userService = inject(UserService);
	private manager = inject(MitpManagerService);
	private toastr = inject(NotificationService);
	private translate = inject(TranslateService);

	notes: ProtocolNote[] = [];
	isLoading = true;
	/** Vrai dès qu'une note a été touchée : le visualiseur doit alors se rafraîchir. */
	hasChanges = false;

	private readonly currentUserId = this.userService.getUserId();

	constructor() {
		this.load();
	}

	/** Seul l'auteur peut modifier ou supprimer ; un manager ne fait que lire. */
	isMine(note: ProtocolNote): boolean {
		return note.user?.id === this.currentUserId;
	}

	isAnchored(note: ProtocolNote): boolean {
		return isTextQuoteAnchor(note.selection_anchor);
	}

	/** Une ancre écrite sur une version antérieure ne vaut plus pour le texte affiché. */
	canLocate(note: ProtocolNote): boolean {
		return this.isAnchored(note) && !note.outdated;
	}

	locate(note: ProtocolNote): void {
		this.dialogRef.close({ action: "locate", changed: this.hasChanges, note: note } as ProtocolNotesListResult);
	}

	close(): void {
		this.dialogRef.close({ action: "close", changed: this.hasChanges } as ProtocolNotesListResult);
	}

	edit(note: ProtocolNote): void {
		this.dialog
			.open(ProtocolNoteDialogComponent, {
				data: { protocolId: this.data.protocolId, note: note } as ProtocolNoteDialogData,
				width: "600px",
				disableClose: true,
			})
			.afterClosed()
			.pipe(first())
			.subscribe((updated: ProtocolNote | undefined) => {
				if (!updated) {
					return;
				}
				this.hasChanges = true;
				this.notes = this.notes.map((item) => (item.id === updated.id ? updated : item));
			});
	}

	remove(note: ProtocolNote): void {
		this.manager
			.showConfirmDialog(
				this.translate.instant("protocols.notes.delete.confirm"),
				this.translate.instant("core.delete")
			)
			.afterClosed()
			.pipe(first())
			.subscribe((confirmed) => {
				if (!confirmed) {
					return;
				}
				this.noteService
					.deleteNote(note.id)
					.pipe(first())
					.subscribe(() => {
						this.hasChanges = true;
						this.notes = this.notes.filter((item) => item.id !== note.id);
						this.toastr.success(this.translate.instant("protocols.notes.delete.success"));
					});
			});
	}

	private load(): void {
		this.noteService
			.getProtocolNotes(this.data.protocolId)
			.pipe(
				first(),
				finalize(() => (this.isLoading = false))
			)
			.subscribe((notes) => {
				this.notes = notes ?? [];
			});
	}
}
